import { resourcesApi, Resource } from '../services/resources-api';

// Categories from app.ts
const RESOURCE_CATEGORIES = [
  'Content Creation', 'Business', 'Marketing', 'Paid Ads', 'Desarrollo Personal',
  'Programming', 'Video Editing', 'Recording Camera', 'AI Prompts', 'Hyperfocus Dopamine',
  'AdsConcierge', 'Site Projects', 'Personal Brand', 'Journaling', 'GTM', 'Saas',
  'Audience Knowledge', 'Dataset/ads', 'Dataset/Reels', 'Dataset/Stories', 'Dataset/Vlog',
  'Dataset/Thumbnails', 'Inspiracion', 'Ideas Contenido', 'Uncategorized'
];

const RESOURCE_TYPES = ['Notes', 'Resource', 'Content', 'Multimedia'];
const CONTENT_FORMATS = ['Reel', 'Video', 'Carousel', 'Image', 'Story', 'Thread', 'Blog Post', 'Article', 'Newsletter', 'Podcast'];
const HOOK_TYPES = ['question', 'contrast', 'mystery', 'statistic', 'story', 'challenge', 'promise', 'curiosity'];

export interface ClassificationData {
  id: string;
  type?: string;
  resourceCategory?: string;
  tags?: string[];
  summary?: string;
  contentFormat?: string;
  hookType?: string;
  confidence?: number;
  content?: string;
  title?: string;
}

interface ClassificationReviewPanelOptions {
  onAccept?: (id: string) => void;
  onCancel?: () => void;
  onSave?: (id: string, data: Partial<ClassificationData>) => void;
  showContentPreview?: boolean;
}

export class ClassificationReviewPanel {
  private container: HTMLElement;
  private data: ClassificationData;
  private options: ClassificationReviewPanelOptions;
  private editedTags: string[] = [];

  constructor(container: HTMLElement, data: ClassificationData, options: ClassificationReviewPanelOptions = {}) {
    this.container = container;
    this.data = data;
    this.options = {
      showContentPreview: true,
      ...options
    };
    this.editedTags = [...(data.tags || [])];
  }

  render(): void {
    const confidence = this.data.confidence ?? 0;
    const confidencePercent = Math.round(confidence * 100);
    const confidenceColor = confidencePercent >= 80 ? '#22c55e' : confidencePercent >= 50 ? '#f59e0b' : '#ef4444';
    const confidenceWidth = Math.max(5, confidencePercent);

    this.container.innerHTML = `
      <div class="classification-panel">
        <div class="classification-panel-header">
          <h3>Clasificación</h3>
          <div class="classification-confidence">
            <span class="confidence-label">Confianza:</span>
            <div class="confidence-bar">
              <div class="confidence-fill" style="width: ${confidenceWidth}%; background: ${confidenceColor}"></div>
            </div>
            <span class="confidence-value" style="color: ${confidenceColor}">${confidencePercent}%</span>
          </div>
        </div>

        <div class="classification-panel-body">
          <div class="classification-field">
            <label>Tipo</label>
            <select id="classification-type">
              ${RESOURCE_TYPES.map(t => `<option value="${t}" ${t === this.data.type ? 'selected' : ''}>${t}</option>`).join('')}
            </select>
          </div>

          <div class="classification-field">
            <label>Categoría</label>
            <select id="classification-category">
              ${RESOURCE_CATEGORIES.map(c => `<option value="${c}" ${c === this.data.resourceCategory ? 'selected' : ''}>${c}</option>`).join('')}
            </select>
          </div>

          <div class="classification-field">
            <label>Tags</label>
            <div class="classification-tags" id="classification-tags">
              ${this.editedTags.map(tag => `
                <span class="classification-tag">
                  ${tag}
                  <button class="tag-remove" data-tag="${tag}">&times;</button>
                </span>
              `).join('')}
              <input type="text" class="tag-input" id="tag-input" placeholder="+ añadir tag" />
            </div>
          </div>

          <div class="classification-field">
            <label>Formato</label>
            <select id="classification-format">
              <option value="">— Sin especificar —</option>
              ${CONTENT_FORMATS.map(f => `<option value="${f}" ${f === this.data.contentFormat ? 'selected' : ''}>${f}</option>`).join('')}
            </select>
          </div>

          <div class="classification-field">
            <label>Summary</label>
            <textarea id="classification-summary" rows="3">${this.data.summary || ''}</textarea>
          </div>

          ${this.options.showContentPreview && this.data.content ? `
            <div class="classification-field">
              <label>Preview contenido</label>
              <div class="classification-preview">${this.escapeHtml(this.data.content.slice(0, 500))}${this.data.content.length > 500 ? '...' : ''}</div>
            </div>
          ` : ''}
        </div>

        <div class="classification-panel-footer">
          <button class="classification-btn classification-btn-secondary" id="btn-cancel">Cancelar</button>
          <button class="classification-btn classification-btn-primary" id="btn-accept">Aceptar</button>
        </div>
      </div>

      <style>
        .classification-panel {
          background: var(--bg-main);
          border-radius: 8px;
          border: 1px solid var(--border);
          overflow: hidden;
        }
        .classification-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          background: var(--bg-panel);
        }
        .classification-panel-header h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
        }
        .classification-confidence {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .confidence-label {
          font-size: 12px;
          color: var(--text-secondary);
        }
        .confidence-bar {
          width: 80px;
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .confidence-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s;
        }
        .confidence-value {
          font-size: 12px;
          font-weight: 600;
          min-width: 32px;
        }
        .classification-panel-body {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          max-height: 60vh;
          overflow-y: auto;
        }
        .classification-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .classification-field label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .classification-field select,
        .classification-field textarea {
          padding: 8px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          background: var(--bg-main);
          color: var(--text-main);
        }
        .classification-field select:focus,
        .classification-field textarea:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        .classification-field textarea {
          resize: vertical;
          min-height: 60px;
          font-family: inherit;
        }
        .classification-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 8px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--bg-main);
          min-height: 38px;
          align-items: center;
        }
        .classification-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          background: var(--primary);
          color: white;
          border-radius: 4px;
          font-size: 12px;
        }
        .tag-remove {
          background: none;
          border: none;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          padding: 0 2px;
          font-size: 14px;
          line-height: 1;
        }
        .tag-remove:hover {
          color: white;
        }
        .tag-input {
          border: none;
          background: none;
          outline: none;
          font-size: 12px;
          padding: 3px;
          min-width: 80px;
          flex: 1;
          color: var(--text-main);
        }
        .tag-input::placeholder {
          color: var(--text-muted);
        }
        .classification-preview {
          padding: 10px;
          background: var(--bg-panel);
          border-radius: 6px;
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.5;
          max-height: 120px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .classification-panel-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid var(--border);
          background: var(--bg-panel);
        }
        .classification-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.15s;
        }
        .classification-btn-secondary {
          background: var(--bg-main);
          color: var(--text-main);
          border: 1px solid var(--border);
        }
        .classification-btn-secondary:hover {
          background: var(--bg-hover);
        }
        .classification-btn-primary {
          background: var(--primary);
          color: white;
        }
        .classification-btn-primary:hover {
          background: #1d4ed8;
        }
      </style>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    // Accept button - GUARDA y cierra
    this.container.querySelector('#btn-accept')?.addEventListener('click', async () => {
      await this.saveCorrection();
      this.options.onAccept?.(this.data.id);
    });

    // Cancel button - solo cierra sin guardar
    this.container.querySelector('#btn-cancel')?.addEventListener('click', () => {
      this.options.onCancel?.();
    });

    // Tag removal
    this.container.querySelectorAll('.tag-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tag = (e.currentTarget as HTMLElement).dataset.tag;
        if (tag) {
          this.editedTags = this.editedTags.filter(t => t !== tag);
          this.renderTags();
        }
      });
    });

    // Tag input
    const tagInput = this.container.querySelector('#tag-input') as HTMLInputElement;
    tagInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const value = tagInput.value.trim().replace(',', '');
        if (value && !this.editedTags.includes(value)) {
          this.editedTags.push(value);
          tagInput.value = '';
          this.renderTags();
        }
      }
    });
  }

  private renderTags(): void {
    const tagsContainer = this.container.querySelector('#classification-tags');
    if (!tagsContainer) return;

    const tagInput = this.container.querySelector('#tag-input') as HTMLInputElement;
    const inputValue = tagInput?.value || '';

    tagsContainer.innerHTML = `
      ${this.editedTags.map(tag => `
        <span class="classification-tag">
          ${tag}
          <button class="tag-remove" data-tag="${tag}">&times;</button>
        </span>
      `).join('')}
      <input type="text" class="tag-input" id="tag-input" placeholder="+ añadir tag" value="${inputValue}" />
    `;

    // Re-bind events for new elements
    tagsContainer.querySelectorAll('.tag-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tag = (e.currentTarget as HTMLElement).dataset.tag;
        if (tag) {
          this.editedTags = this.editedTags.filter(t => t !== tag);
          this.renderTags();
        }
      });
    });

    const newTagInput = tagsContainer.querySelector('#tag-input') as HTMLInputElement;
    newTagInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const value = newTagInput.value.trim().replace(',', '');
        if (value && !this.editedTags.includes(value)) {
          this.editedTags.push(value);
          newTagInput.value = '';
          this.renderTags();
        }
      }
    });
    newTagInput?.focus();
  }

  private async saveCorrection(): Promise<void> {
    const type = (this.container.querySelector('#classification-type') as HTMLSelectElement)?.value;
    const resourceCategory = (this.container.querySelector('#classification-category') as HTMLSelectElement)?.value;
    const contentFormat = (this.container.querySelector('#classification-format') as HTMLSelectElement)?.value;
    const summary = (this.container.querySelector('#classification-summary') as HTMLTextAreaElement)?.value;

    const correctionData: Partial<ClassificationData> = {
      type,
      resourceCategory,
      tags: this.editedTags,
      summary,
      contentFormat: contentFormat || undefined
    };

    // Call API to update resource with human_review = true
    try {
      await resourcesApi.update(this.data.id, {
        type: type as 'notes' | 'knowledge' | 'multimedia',
        resourceCategory,
        tags: this.editedTags,
        humanReview: true,
        customFields: {
          summary,
          contentFormat: contentFormat || undefined
        }
      });

      this.options.onSave?.(this.data.id, correctionData);
    } catch (error) {
      console.error('Failed to save correction:', error);
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Update data and re-render
  update(data: ClassificationData): void {
    this.data = data;
    this.editedTags = [...(data.tags || [])];
    this.render();
  }
}
